import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products, categories } from "@/mocks/products";
import { useCart } from "@/contexts/CartContext";

type SortOption = "featured" | "price-asc" | "price-desc" | "rating" | "newest";

export default function ProductsPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState<SortOption>("featured");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [addedId, setAddedId] = useState<string | null>(null);
  const { addToCart } = useCart();

  const filtered = useMemo(() => {
    let list = [...products];
    if (selectedCategory !== "All") {
      list = list.filter(p => p.category === selectedCategory);
    }
    list = list.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    switch (sortBy) {
      case "price-asc": list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "rating": list.sort((a, b) => b.rating - a.rating); break;
      default: break;
    }
    return list;
  }, [selectedCategory, sortBy, priceRange]);

  const handleAdd = (product: typeof products[0]) => {
    addToCart(product);
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <Navbar />
      <main className="pt-[72px]">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-100 py-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
              <Link to="/" className="hover:text-[#2D5016] transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#1a1a1a]">Products</span>
            </div>
            <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Outdoor Furniture Collection
            </h1>
            <p className="text-gray-400 mt-2 text-sm">Premium outdoor furniture for every space and style</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6 sticky top-24">
                <h3 className="font-semibold text-[#1a1a1a] mb-5 text-sm uppercase tracking-wide">Filters</h3>

                {/* Category */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Category</h4>
                  <div className="space-y-2">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                          selectedCategory === cat
                            ? "bg-[#EEF5E8] text-[#2D5016] font-medium"
                            : "text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Price Range</h4>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={1000}
                    step={50}
                    value={priceRange[1]}
                    onChange={e => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-full accent-[#2D5016]"
                  />
                </div>

                {/* Reset */}
                <button
                  onClick={() => { setSelectedCategory("All"); setPriceRange([0, 1000]); setSortBy("featured"); }}
                  className="text-[#2D5016] text-sm font-medium hover:underline cursor-pointer"
                >
                  Reset Filters
                </button>
              </div>
            </aside>

            {/* Product Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <span className="text-gray-500 text-sm">
                  <strong className="text-[#1a1a1a]">{filtered.length}</strong> products found
                </span>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortOption)}
                  className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600 focus:outline-none focus:border-[#2D5016] bg-white cursor-pointer"
                >
                  <option value="featured">Featured</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                </select>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map(product => (
                  <div key={product.id} className="group bg-white rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300">
                    <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
                      <div className="w-full h-64">
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      {product.badge && (
                        <span className="absolute top-3 left-3 bg-[#2D5016] text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {product.badge}
                        </span>
                      )}
                      {product.originalPrice && (
                        <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                          Sale
                        </span>
                      )}
                    </Link>
                    <div className="p-5">
                      <span className="text-[#4A7C2F] text-xs font-medium uppercase tracking-wide">{product.category}</span>
                      <Link to={`/product/${product.id}`}>
                        <h3 className="text-[#1a1a1a] font-semibold text-sm mt-1 mb-2 leading-snug hover:text-[#2D5016] transition-colors cursor-pointer line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-1.5 mb-3">
                        <div className="flex items-center gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <i key={s} className={`text-xs ${s <= Math.round(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}></i>
                          ))}
                        </div>
                        <span className="text-gray-400 text-xs">{product.rating} ({product.reviewCount})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xl font-bold text-[#1a1a1a]">${product.price}</span>
                          {product.originalPrice && (
                            <span className="text-gray-400 text-xs line-through">${product.originalPrice}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAdd(product)}
                          className={`flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300 cursor-pointer ${
                            addedId === product.id
                              ? "bg-[#2D5016] text-white"
                              : "bg-[#F0EDE6] hover:bg-[#2D5016] hover:text-white text-[#2D5016]"
                          }`}
                        >
                          <i className={`text-sm ${addedId === product.id ? "ri-check-line" : "ri-shopping-bag-line"}`}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                  <i className="ri-search-line text-4xl mb-3 block"></i>
                  <p>No products match your filters.</p>
                  <button onClick={() => { setSelectedCategory("All"); setPriceRange([0, 1000]); }} className="text-[#2D5016] text-sm mt-2 hover:underline cursor-pointer">
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
